import { axiosInstance } from "./index.ts";

export type EngagementStatus =
	| "pending_terms"
	| "terms_accepted"
	| "in_progress"
	| "deliverables_submitted"
	| "cm_approved"
	| "sent_to_finance"
	| "completed"
	| "on_hold"
	| "cancelled"
	| "declined"
	| "legacy";

export interface CreatorEngagementListItem {
	engagementId: string;
	campaignId: string;
	campaignName: string;
	isAvailable: boolean;
	projectCode: string | null;
	/** CREATOR block for this assignment; used to scope UI when one user owns multiple personas. */
	creatorBlockId: string | null;
	creatorAssignmentId: string;
	status: EngagementStatus;
	paymentStatus: string;
	/** Finance's reason, present only when the payment was rejected by Finance. */
	paymentStatusReason: string | null;
	paymentStatusUpdatedAt: string | null;
	termsAcceptedAt: string | null;
	hasBankProfile: boolean;
	canAcceptTerms: boolean;
	needsBankProfile: boolean;
	/** Ticket-348: a live tax declaration is required before terms can be accepted and a statement issued. */
	hasTaxDeclaration: boolean;
	needsTaxDeclaration: boolean;
	/** Set at send-to-finance; local_partner means no Payment Statement is issued. */
	paymentRoute: "cpi_direct" | "local_partner" | null;
	latestVersionNumber?: number | null;
	deliverablesTotal: number;
	deliverablesDone: number;
}

export interface CampaignEngagementDeliverable {
	id: string;
	deliverableType: string;
	deliverableTypes: string[];
	title: string;
	description: string | null;
	quantity: number;
	usageRights: string | null;
	dueDate: string | null;
	isComplete: boolean;
	completedAt: string | null;
}

export interface CampaignEngagementSchedule {
	id: string;
	deliverableId: string | null;
	milestoneKey: string;
	label: string;
	dueDate: string;
}

export type DeclineTermsReasonCode = "fee" | "schedule" | "brand_conflict" | "brand_align" | "other";

export interface DeclineTermsInput {
	reasonCodes: DeclineTermsReasonCode[];
	note?: string;
}

export interface CampaignEngagementTerms {
	id: string;
	projectCode: string;
	paymentTerms: string;
	effectiveStart: string;
	effectiveEnd: string;
	exclusivityType: string;
	exclusivityCustom: string | null;
	creatorFeeAmount: number;
	creatorFeeCurrency: string;
	services: string | null;
	presentedAt: string | null;
	versionNumber: number;
	responseStatus: "draft" | "pending" | "accepted" | "declined" | "superseded";
	respondedAt: string | null;
	responseReason: string | null;
	responseReasonCode: string | null;
	responseNote: string | null;
	changeSummary: string | null;
	deliverables: CampaignEngagementDeliverable[];
	schedules: CampaignEngagementSchedule[];
}

export interface CreatorEngagementDetail extends CreatorEngagementListItem {
	acceptedTermsId: string | null;
	marketBlockId?: string | null;
	terms: CampaignEngagementTerms | null;
	verifiedDeliverableIds: string[];
	creatorName: string;
	creatorEmail: string | null;
	creatorPhone: string | null;
	creatorAvatarUrl: string | null;
	brand: string | null;
	marketName: string | null;
	liveDate: string | null;
	campaignManagerName?: string | null;
	campaignSpecialistName?: string | null;
	approvedByName?: string | null;
	approvedAt?: string | null;
	sentToFinanceAt?: string | null;
}

export interface PaymentInstructionSummary {
	id: string;
	status: string;
	instructedAt: string;
}

export interface MaskedPaymentProfile {
	id: string;
	country: string;
	bankName: string;
	accountNameMasked: string;
	accountNumberLast4: string;
	/** Full account number (owner-facing decrypt). */
	accountNumber?: string;
	accountNumberUnavailable?: boolean;
	swiftBic: string | null;
	currency: string | null;
	isActive: boolean;
	consentRecordedAt: string;
	updatedAt: string;
	localRouting?: Record<string, string>;
	taxId?: string;
}

export interface PaymentProfileInput {
	country: string;
	bankName: string;
	accountName: string;
	accountNumber: string;
	swiftBic?: string;
	currency?: string;
	localRouting?: Record<string, string>;
	taxId?: string;
}

export const creatorCpiAPI = {
	getAgencyEngagement: async (campaignId: string, creatorBlockId: string): Promise<CreatorEngagementDetail> => {
		const res = await axiosInstance.get(`/campaigns/${campaignId}/creator-blocks/${creatorBlockId}/engagement`);
		return res.data.engagement;
	},
	approveAgencyEngagement: async (campaignId: string, creatorBlockId: string): Promise<CreatorEngagementDetail> => {
		const res = await axiosInstance.patch(
			`/campaigns/${campaignId}/creator-blocks/${creatorBlockId}/engagement/status`,
			{ action: "approve" },
		);
		return res.data.engagement;
	},
	sendAgencyEngagementToFinance: async (
		campaignId: string,
		creatorBlockId: string,
	): Promise<{ engagement: CreatorEngagementDetail; paymentInstruction: PaymentInstructionSummary }> => {
		const res = await axiosInstance.post(
			`/campaigns/${campaignId}/creator-blocks/${creatorBlockId}/engagement/send-to-finance`,
		);
		return res.data;
	},

	listMyEngagements: async (): Promise<CreatorEngagementListItem[]> => {
		const res = await axiosInstance.get("/creators/me/engagements");
		return res.data.engagements ?? [];
	},

	getEngagement: async (engagementId: string, latest = false): Promise<CreatorEngagementDetail> => {
		const res = await axiosInstance.get(`/campaign-engagements/${engagementId}`, {
			params: latest ? { latest: true } : undefined,
		});
		return res.data.engagement;
	},

	listEngagementTermVersions: async (engagementId: string): Promise<CampaignEngagementTerms[]> => {
		const res = await axiosInstance.get(`/campaign-engagements/${engagementId}/terms/versions`);
		return res.data.versions ?? [];
	},

	listPaymentProfiles: async (): Promise<MaskedPaymentProfile[]> => {
		const res = await axiosInstance.get("/creators/me/payment-profile");
		if (Array.isArray(res.data.profiles)) {
			return res.data.profiles;
		}
		return res.data.profile ? [res.data.profile] : [];
	},

	getPaymentProfile: async (): Promise<MaskedPaymentProfile | null> => {
		const profiles = await creatorCpiAPI.listPaymentProfiles();
		return profiles.find((p) => p.isActive) ?? profiles[0] ?? null;
	},

	addPaymentProfile: async (input: PaymentProfileInput, timeout?: number): Promise<MaskedPaymentProfile> => {
		const res = await axiosInstance.put("/creators/me/payment-profile", input, { timeout });
		return res.data.profile;
	},

	updatePaymentProfile: async (
		profileId: string,
		input: PaymentProfileInput,
		timeout?: number,
	): Promise<MaskedPaymentProfile> => {
		const res = await axiosInstance.put(`/creators/me/payment-profile/${profileId}`, input, { timeout });
		return res.data.profile;
	},

	/** @deprecated use addPaymentProfile */
	upsertPaymentProfile: async (input: PaymentProfileInput): Promise<MaskedPaymentProfile> => {
		return creatorCpiAPI.addPaymentProfile(input);
	},

	setDefaultPaymentProfile: async (profileId: string): Promise<MaskedPaymentProfile> => {
		const res = await axiosInstance.post(`/creators/me/payment-profile/${profileId}/default`);
		return res.data.profile;
	},

	deletePaymentProfile: async (profileId: string): Promise<MaskedPaymentProfile[]> => {
		const res = await axiosInstance.delete(`/creators/me/payment-profile/${profileId}`);
		return Array.isArray(res.data.profiles) ? res.data.profiles : [];
	},

	acceptTerms: async (engagementId: string): Promise<CreatorEngagementDetail> => {
		const res = await axiosInstance.post(`/campaign-engagements/${engagementId}/accept-terms`);
		return res.data.engagement;
	},

	declineTerms: async (engagementId: string, input: DeclineTermsInput): Promise<CreatorEngagementDetail> => {
		const res = await axiosInstance.post(`/campaign-engagements/${engagementId}/decline-terms`, input);
		return res.data.engagement;
	},
};
